/**
 * `ListToolbar` (DNKADM-0430): the one toolbar above every list: search, filter slots,
 * an optional From/To date range, and Clear, in the same order and look on every screen.
 *
 * It is presentational only. The screen owns the values (normally through
 * `useListUrlState`, so they live in the URL) and decides what each control does: a
 * client-side list filters its loaded rows, a server-paged list passes the values to its
 * API call. Date values are `YYYY-MM-DD` strings from a native date input; mapping them to
 * an endpoint's param names (`from`/`fromDate`/`scheduledFrom`...) belongs in the API
 * client, not here.
 *
 * Every part is optional, so a screen renders only what it has:
 * - `search`: the text box with the search icon. It types into a local draft and reports
 *   each keystroke through `onChange`; see {@link ToolbarSearch} for why.
 * - `filters`: a slot for selects, switches or toggles. Use {@link ListSelectFilter} for
 *   an "All ..." select so filters look alike.
 * - `dateRange`: From and To native date inputs.
 * - `onClear`: shows a Clear button while `canClear` is true (by default, while the search
 *   or a date has a value). The screen's handler resets its search, filters, dates and
 *   page in one `set` call; it leaves the tab and the sort alone.
 * - `end`: a slot pushed to the right (a row count, an export button).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, InputAdornment, MenuItem, Stack, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, resolveTokens } from '@/theme';

export interface ListToolbarSearch {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  /** Accessible name; defaults to `placeholder`. */
  label?: string;
  disabled?: boolean;
}

export interface ListToolbarDateRange {
  /** `YYYY-MM-DD` or `''`. */
  from: string;
  /** `YYYY-MM-DD` or `''`. */
  to: string;
  onFromChange: (next: string) => void;
  onToChange: (next: string) => void;
  fromLabel?: string;
  toLabel?: string;
  disabled?: boolean;
}

export interface ListToolbarProps {
  search?: ListToolbarSearch;
  filters?: ReactNode;
  dateRange?: ListToolbarDateRange;
  onClear?: () => void;
  /** Whether Clear shows. Defaults to: the search or either date has a value. */
  canClear?: boolean;
  end?: ReactNode;
}

export function ListToolbar({
  search,
  filters,
  dateRange,
  onClear,
  canClear,
  end,
}: ListToolbarProps) {
  const t = resolveTokens(useTheme());
  const hasValue =
    (search?.value.length ?? 0) > 0 ||
    (dateRange?.from.length ?? 0) > 0 ||
    (dateRange?.to.length ?? 0) > 0;
  const showClear = onClear !== undefined && (canClear ?? hasValue);

  return (
    <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5, my: 2 }}>
      {search ? <ToolbarSearch search={search} /> : null}
      {filters}
      {dateRange ? (
        <>
          <TextField
            type="date"
            size="small"
            label={dateRange.fromLabel ?? 'From'}
            value={dateRange.from}
            onChange={(e) => dateRange.onFromChange(e.target.value)}
            disabled={dateRange.disabled}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ minWidth: 160, bgcolor: t.surface }}
          />
          <TextField
            type="date"
            size="small"
            label={dateRange.toLabel ?? 'To'}
            value={dateRange.to}
            onChange={(e) => dateRange.onToChange(e.target.value)}
            disabled={dateRange.disabled}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ minWidth: 160, bgcolor: t.surface }}
          />
        </>
      ) : null}
      {showClear ? (
        <Button color="primary" startIcon={<Icon name="close" />} onClick={onClear}>
          Clear
        </Button>
      ) : null}
      {end ? <Box sx={{ ml: 'auto' }}>{end}</Box> : null}
    </Stack>
  );
}

/**
 * The toolbar's search box. It renders a LOCAL draft rather than `search.value`.
 *
 * Why: the value normally lives in the URL, and react-router commits a navigation inside a
 * transition, after the input event. A box controlled straight from the URL is therefore
 * reset to the old value for a moment on every keystroke, which moves the caret to the end
 * and can drop characters from a fast typist.
 *
 * So the draft updates on every keystroke, and each value it reports is remembered. When
 * `search.value` changes, a value this box reported (the URL catching up with its own
 * typing) is an echo: it and every earlier report are forgotten and the draft is left
 * alone, so a late echo never overwrites newer typing. Any other value (Clear, a tab
 * change, Back/forward, a deep link) is adopted into the draft.
 */
function ToolbarSearch({ search }: { search: ListToolbarSearch }) {
  const t = resolveTokens(useTheme());
  const { value, onChange } = search;
  const [draft, setDraft] = useState(value);
  // Values reported through `onChange` that the prop has not echoed back yet, oldest first.
  const pending = useRef<string[]>([]);

  useEffect(() => {
    const echo = pending.current.indexOf(value);
    if (echo >= 0) {
      pending.current = pending.current.slice(echo + 1);
      return;
    }
    pending.current = [];
    setDraft(value);
  }, [value]);

  return (
    <TextField
      size="small"
      value={draft}
      onChange={(e) => {
        const next = e.target.value;
        setDraft(next);
        pending.current.push(next);
        onChange(next);
      }}
      placeholder={search.placeholder}
      disabled={search.disabled}
      sx={{ flex: 1, minWidth: 220, maxWidth: 420, bgcolor: t.surface }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Icon name="search" sx={{ color: t.textMuted }} />
            </InputAdornment>
          ),
        },
        htmlInput: { 'aria-label': search.label ?? search.placeholder },
      }}
    />
  );
}

/** The sentinel value of a {@link ListSelectFilter}'s "All ..." option. */
const ALL_OPTION = '';

/**
 * A select filter for the toolbar's `filters` slot, with a leading "All ..." option whose
 * value is `''` (so an unfiltered select clears its URL param).
 */
export function ListSelectFilter({
  label,
  value,
  onChange,
  allLabel,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  allLabel: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  disabled?: boolean;
}) {
  const t = resolveTokens(useTheme());
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
      sx={{ minWidth: 180, bgcolor: t.surface }}
    >
      <MenuItem value={ALL_OPTION}>{allLabel}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
